import React from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowRight, CheckCircle2, ChevronDown } from "lucide-react";
import { SERVICES, SUB_SERVICES, IMAGES, CITIES } from "@/data/content";
import { DEEP_SERVICES } from "@/data/deepServices";
import { SectionHeading, CTA, FinalCTA } from "@/components/Shared";
import { SEO, serviceSchema, faqSchema, breadcrumbSchema } from "@/components/SEO";
import { ShinglePattern, RoofDivider, RoofAccent } from "@/components/RoofAccents";
import { DeepServicePage } from "@/pages/DeepServicePages";

function FAQItem({ q, a }) {
  const [open, setOpen] = React.useState(false);
  return (
    <div className="border-b border-ink-line">
      <button onClick={() => setOpen(!open)} className="w-full text-left py-5 flex justify-between items-start gap-4" data-testid={`faq-toggle-${q.slice(0,20).replace(/\s/g,'-')}`}>
        <span className="font-heading text-lg font-bold">{q}</span>
        <ChevronDown className={`w-5 h-5 flex-shrink-0 mt-1 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && <div className="pb-5 text-ink-soft leading-relaxed">{a}</div>}
    </div>
  );
}

export function ServicesHubPage() {
  return (
    <div>
      <SEO
        title="Roofing & Siding Services in Oakland County, MI"
        description="Roof replacement, repair, storm damage, siding, gutters & insurance claims across Oakland County, MI. Licensed, insured, Master Certified."
        path="/services"
      />
      <section className="bg-bg-dark text-white py-16 md:py-28 relative overflow-hidden group">
        {/* Ambient roof-line accent - subtle at rest, brightens on hover */}
        <svg aria-hidden="true" className="absolute -right-16 top-8 w-[520px] h-[260px] opacity-[0.05] group-hover:opacity-[0.12] transition-opacity duration-700 pointer-events-none" viewBox="0 0 520 260" fill="none" stroke="currentColor" strokeWidth="1.2">
          <path d="M40 200 L140 100 L240 200 L340 80 L440 200 L500 140" />
          <path d="M60 200 L60 230 L460 230 L460 200" />
          <rect x="190" y="160" width="80" height="70" />
          <path d="M190 190 L270 190 M230 160 L230 230" />
          <path d="M380 80 L380 60 L400 60 L400 100" />
        </svg>
        <div className="container-x relative">
          <div className="overline text-brand">Services</div>
          <h1 className="font-heading font-extrabold text-4xl sm:text-5xl md:text-7xl tracking-tight mt-3 max-w-3xl leading-[1.02]">Five core services. One trusted family crew.</h1>
          <p className="text-white/80 text-base md:text-lg max-w-2xl mt-4 md:mt-5 leading-relaxed">From a single leak to a full roof replacement, siding, gutters, and insurance-claim work - we handle the scope end-to-end.</p>
          <div className="mt-6 md:mt-8"><CTA dark /></div>
        </div>
      </section>

      <section className="py-20">
        <div className="container-x grid md:grid-cols-2 gap-px bg-ink-line border border-ink-line">
          {SERVICES.map(s => (
            <Link to={`/services/${s.slug}`} key={s.slug} className="bg-white p-10 hover:bg-bg-soft group" data-testid={`services-hub-${s.slug}`}>
              <div className="aspect-[16/10] overflow-hidden -mx-10 -mt-10 mb-6">
                <img src={s.image} alt={s.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
              </div>
              <div className="overline">{s.short}</div>
              <h3 className="font-heading text-3xl font-extrabold mt-2">{s.name}</h3>
              <p className="text-ink-soft mt-3 leading-relaxed">{s.hero}</p>
              <div className="mt-5 text-brand font-semibold flex items-center gap-2">Learn more <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" /></div>
            </Link>
          ))}
        </div>
      </section>

      <section className="py-20 bg-bg-soft">
        <div className="container-x">
          <SectionHeading overline="Sub-services" title="Need something specific?" sub="Deep-dive pages for materials, repair types, and specialty work." />
          <div className="mt-10 grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            {SUB_SERVICES.map((s, i) => {
              const accents = ["gable", "window", "shingles", "door", "vane", "skyline"];
              return (
                <Link to={`/services/${s.slug}`} key={s.slug} className="card-base p-5 relative overflow-hidden group hover:border-brand/40 transition-colors" data-testid={`sub-service-${s.slug}`}>
                  <RoofAccent variant={accents[i % accents.length]} position="bottom-right" size="sm" intensity="soft" />
                  <div className="font-heading text-lg font-bold relative">{s.name}</div>
                  <div className="text-sm text-ink-soft mt-1 relative">{s.short}</div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* Specialty repair pages — high-volume Michigan keywords surfaced by the
          Semrush competitor audit (May 2026). Linking here ensures every deep
          SEO silo page has a site-wide internal link and is not orphaned. */}
      <section className="py-20 border-t border-ink-line">
        <div className="container-x">
          <SectionHeading
            overline="Specialty repairs"
            title="Specific repair we handle every week"
            sub="Each page below is its own walkthrough: when you need it, what it costs in Michigan, how insurance treats it, and the FAQ Michigan homeowners actually ask."
          />
          <div className="mt-10 grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {DEEP_SERVICES.map((d) => (
              <Link
                to={`/services/${d.slug}`}
                key={d.slug}
                className="card-base p-5 hover:border-brand transition-colors group"
                data-testid={`deep-service-card-${d.slug}`}
              >
                <div className="overline text-brand">Specialty</div>
                <div className="font-heading text-lg font-bold mt-2 group-hover:text-brand transition-colors">
                  {d.keyword.replace(/\b\w/g, (c) => c.toUpperCase())}
                </div>
                <div className="text-sm text-ink-soft mt-2 leading-relaxed line-clamp-3">{d.description}</div>
                <div className="mt-3 text-sm text-brand font-semibold inline-flex items-center gap-1">
                  Learn more <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>
      <FinalCTA />
    </div>
  );
}

export function ServiceDetailPage() {
  const { slug } = useParams();
  // 1) Deep SEO silo pages (high-volume / low-KD keywords from Semrush audit)
  //    — render first so they take precedence over any future slug collisions.
  if (DEEP_SERVICES.some((d) => d.slug === slug)) {
    return <DeepServicePage />;
  }
  const main = SERVICES.find(s => s.slug === slug);
  const sub = SUB_SERVICES.find(s => s.slug === slug);

  if (main) {
    // Per-service unique H1 + title (≤60 chars incl. suffix) + meta (≤160 chars)
    const seoMap = {
      "residential-roofing": {
        h1: "Professional Residential Roofing in Oakland County, MI",
        title: "Residential Roofing in Oakland County MI | Goral Construction",
        desc: "Trusted residential roofing in Oakland County, MI. CertainTeed Landmark architectural shingles, 10-yr workmanship + 50-yr manufacturer warranty. Free estimates!",
      },
      "roof-replacement": {
        h1: "Professional Roof Replacement in Oakland County, MI",
        title: "Roof Replacement in Oakland County MI | Goral Construction",
        desc: "Full tear-off roof replacement in Oakland County, MI. Landmark Pro shingles, WinterGuard ice & water shield, 1-2 day install, 10-yr warranty. Call today!",
      },
      "roof-repair": {
        h1: "Professional Roof Repair in Oakland County, MI",
        title: "Roof Repair in Oakland County MI | Goral Construction",
        desc: "24-hr emergency roof repair in Oakland County, MI. Leaks, missing shingles, flashing, hail & wind damage. Free inspection. Call (248) 804-6933!",
      },
      "storm-damage": {
        h1: "Professional Storm Damage Restoration in Oakland County, MI",
        title: "Storm Damage Roofing in Oakland County MI | Goral",
        desc: "24-hr emergency tarping + full hail/wind storm damage restoration in Oakland County, MI. Adjuster meetings, insurance claim help. Call today!",
      },
      "insurance-claims": {
        h1: "Roof Insurance Claim Help in Oakland County, MI",
        title: "Roof Insurance Claims in Oakland County MI | Goral",
        desc: "End-to-end roof insurance claim help in Oakland County, MI. Photo docs, adjuster meetings, supplements. We only get paid when your claim is approved!",
      },
      "siding": {
        h1: "Professional Siding Installation in Oakland County, MI",
        title: "Siding Installation in Oakland County MI | Goral",
        desc: "Vinyl, James Hardie fiber cement & LP SmartSide engineered wood siding in Oakland County, MI. Licensed, insured, 10-yr warranty. Free estimate!",
      },
      "gutters": {
        h1: "Professional Seamless Gutters & Guards in Oakland County, MI",
        title: "Seamless Gutters in Oakland County MI | Goral",
        desc: "Seamless 5\" & 6\" K-style aluminum gutters + micro-mesh guards across Oakland County, MI. Licensed, insured, free on-site measurement. Call today!",
      },
    };
    const seo = seoMap[main.slug] || { h1: main.name, title: `${main.name} in Oakland County, MI`, desc: main.hero };
    return (
      <div>
        <SEO
          title={seo.title}
          description={seo.desc}
          path={`/services/${main.slug}`}
          image={main.image}
          schema={{
            "@context": "https://schema.org",
            "@graph": [
              serviceSchema({ name: main.name, description: main.hero, path: `/services/${main.slug}` }),
              faqSchema(main.faqs),
              breadcrumbSchema([
                { name: "Home", path: "/" },
                { name: "Services", path: "/services" },
                { name: main.name, path: `/services/${main.slug}` },
              ]),
            ],
          }}
        />
        <section className="relative bg-bg-dark text-white overflow-hidden">
          <div className="absolute inset-0">
            <img src={main.image} alt={main.name} className="w-full h-full object-cover opacity-45" />
            <div className="absolute inset-0 bg-gradient-to-r from-black/85 via-black/55 to-transparent" />
          </div>
          <div className="relative container-x py-16 md:py-32">
            <Link to="/services" className="overline text-brand hover:text-white">← All Services</Link>
            <h1 className="font-heading font-extrabold text-4xl md:text-6xl tracking-tight mt-4 max-w-4xl leading-[1.05]">{seo.h1}</h1>
            <p className="text-white/85 text-lg md:text-xl max-w-2xl mt-5 leading-relaxed">{main.hero}</p>
            <div className="mt-8"><CTA dark /></div>
          </div>
        </section>

        <section className="py-20 md:py-28">
          <div className="container-x grid lg:grid-cols-5 gap-14">
            <div className="lg:col-span-3">
              <h2 className="font-heading text-3xl md:text-4xl font-extrabold tracking-tight">{main.word}</h2>
              <p className="mt-5 text-lg text-ink-soft leading-relaxed">{main.intro}</p>
              <div className="mt-10 space-y-4">
                {main.features.map(f => (
                  <div key={f} className="flex gap-3 items-start"><CheckCircle2 className="w-6 h-6 text-brand flex-shrink-0 mt-0.5" /><span className="text-ink font-medium">{f}</span></div>
                ))}
              </div>
            </div>
            <aside className="lg:col-span-2 lg:sticky lg:top-24 h-fit">
              <div className="border border-ink-line bg-white p-6">
                <div className="overline">Free estimate</div>
                <div className="font-heading text-2xl font-extrabold mt-2">Get a quote today</div>
                <p className="text-sm text-ink-soft mt-2">Written, itemized estimate within 24-48 hours of your inspection.</p>
                <div className="mt-5 flex flex-col gap-2">
                  <Link to="/get-estimate" className="btn-primary w-full justify-center">Start estimate <ArrowRight className="w-4 h-4" /></Link>
                </div>
              </div>
            </aside>
          </div>
        </section>

        <section className="py-20 bg-bg-soft">
          <div className="container-x">
            <SectionHeading overline="Our process" title={`How a ${main.name.toLowerCase()} job works with us`} />
            <div className="mt-10 grid md:grid-cols-5 gap-px bg-ink-line border border-ink-line">
              {main.process.map((p, i) => {
                const accents = ["gable", "window", "shingles", "door", "vane"];
                return (
                  <div key={p.title} className="group relative bg-white p-6 hover:bg-bg-soft transition-colors overflow-hidden">
                    <RoofAccent variant={accents[i % accents.length]} position="bottom-right" size="md" intensity="soft" />
                    <div className="font-heading text-5xl font-extrabold text-brand/30 relative">{String(i + 1).padStart(2, "0")}</div>
                    <div className="font-heading text-lg font-bold mt-3 relative">{p.title}</div>
                    <div className="text-sm text-ink-soft mt-2 leading-relaxed relative">{p.body}</div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        <section className="py-20">
          <div className="container-x grid md:grid-cols-3 gap-10">
            <div><SectionHeading overline="FAQs" title={`${main.name} questions`} /></div>
            <div className="md:col-span-2">
              {main.faqs.map(f => <FAQItem key={f.q} q={f.q} a={f.a} />)}
            </div>
          </div>
        </section>

        {/* Trust signals: Materials We Use - critical for AI Overview surface */}
        <section className="py-16 md:py-20 bg-bg-soft border-t border-ink-line">
          <div className="container-x">
            <SectionHeading overline="Roofing materials we use" title="Premium products. Manufacturer-backed installs." sub="We install with manufacturer-certified components so the warranty actually pays out if you ever need it." />
            <div className="mt-10 grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {[
                { t: "CertainTeed Landmark & Landmark Pro", d: "Architectural shingles rated for 110-130 mph winds with 30-50 year warranty when installed to spec." },
                { t: "WinterGuard Ice & Water Shield", d: "Self-sealing ice & water membrane on every eave and valley - 6 ft minimum, exceeds Michigan code." },
                { t: "RoofRunner Synthetic Underlayment", d: "Tear-resistant synthetic underlayment on the entire deck. Replaces old felt that rots within a decade." },
                { t: "Ridge Vents & Attic Ventilation", d: "Continuous ridge venting balanced with soffit intake - the #1 fix for ice dams and premature shingle failure." },
                { t: "10-Year Workmanship Warranty", d: "Our written workmanship warranty is transferable once to the next homeowner. No fine print." },
                { t: "Step & Counter Flashing", d: "Factory-spec step flashing on every wall, chimney, and skylight. The detail that keeps a roof watertight for decades." },
              ].map(m => (
                <div key={m.t} className="card-base p-5">
                  <div className="font-heading text-lg font-bold">{m.t}</div>
                  <div className="text-sm text-ink-soft mt-2 leading-relaxed">{m.d}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Areas We Serve - internal linking + GEO depth.
            We link to the DEEP /services/X/in/CITY combo pages (not the city hub)
            so every service×city page is reachable in one click from the service
            hub. This resolves the Semrush "orphaned page in sitemap" warnings for
            all 72 combo URLs. */}
        <section className="py-16 md:py-20">
          <div className="container-x">
            <SectionHeading overline="Areas we serve" title={`${main.name} across Oakland County, MI`} sub="We bring the same crew, materials and warranty to every Oakland County zip code we cover." />
            <div className="mt-8 grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
              {CITIES.map(c => (
                <Link
                  key={c.slug}
                  to={`/services/${main.slug}/in/${c.slug}`}
                  className="px-4 py-3 border border-ink-line bg-white hover:border-brand hover:text-brand text-sm font-semibold rounded-sm transition-colors"
                  data-testid={`service-city-link-${c.slug}`}
                >
                  {main.name} in {c.name}, MI
                </Link>
              ))}
            </div>
          </div>
        </section>

        <FinalCTA />
      </div>
    );
  }

  if (sub) {
    const parent = SERVICES.find(s => s.slug === sub.parent);
    return (
      <div>
        <SEO
          title={`${sub.name} Oakland County, MI`}
          description={sub.desc}
          path={`/services/${sub.slug}`}
          image={parent.image}
          schema={serviceSchema({ name: sub.name, description: sub.desc, path: `/services/${sub.slug}`, parent: parent.name })}
        />
        <section className="bg-bg-dark text-white py-20 md:py-28">
          <div className="container-x">
            <Link to={`/services/${parent.slug}`} className="overline text-brand hover:text-white">← {parent.name}</Link>
            <h1 className="font-heading font-extrabold text-5xl md:text-7xl tracking-tight mt-4 max-w-3xl leading-[1.02]">{sub.name}</h1>
            <p className="text-white/85 text-xl max-w-2xl mt-5 leading-relaxed">{sub.desc}</p>
            <div className="mt-8"><CTA dark /></div>
          </div>
        </section>
        <section className="py-20">
          <div className="container-x grid md:grid-cols-2 gap-10 items-start">
            <img src={parent.image} alt={sub.name} className="w-full aspect-[4/3] object-cover" />
            <div>
              <SectionHeading overline={`Part of ${parent.name}`} title={sub.short} />
              <p className="mt-6 text-ink-soft leading-relaxed">{sub.desc} Every project starts with a free on-site inspection and written, line-item estimate. We handle permits, insurance documentation, and cleanup - so you don't have to.</p>
              <div className="mt-6 space-y-3">
                {parent.features.map(f => (
                  <div key={f} className="flex gap-3 items-start"><CheckCircle2 className="w-5 h-5 text-brand mt-0.5" /><span>{f}</span></div>
                ))}
              </div>
              <div className="mt-8"><CTA /></div>
            </div>
          </div>
        </section>
        <FinalCTA />
      </div>
    );
  }

  return <div className="container-x py-32"><h1 className="font-heading text-4xl font-bold">Service not found</h1><Link to="/services" className="btn-primary mt-6">All Services</Link></div>;
}
